/*
 * FreeEvent — the presentational pieces shared by /start, the free-event
 * banner, the plan sheet, the limited dashboard and the first run (design
 * 2026-10-13-free-start-web r9, looks 1A and 2A). Dumb by the repo's rule:
 * props in, markup out. Styles: containers/FreeStart/free-start.css, scoped
 * under `.mb-fs`.
 *
 * Icons are heroicons (already a dependency). The sheet drew Icons8 glyphs
 * under the owner's license; their SVG key isn't vaulted yet (design Req 18).
 */
import React from "react";
import {
  CheckIcon,
  TicketIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import { t } from "copy/t";
import { noWidow } from "utils/eventTime";
import { photoSrc, PHOTO_POSITION } from "./photos";

export const ft = (key, vars) => t(`freeStart.${key}`, vars);
// The same line with its last two words bound (Req 19).
export const fw = (key, vars) => noWidow(ft(key, vars));

// A counted line. The catalog splits counted sentences into whole-sentence
// variant keys (t() reads flat {name} tokens only, no ICU): `key_zero` where
// zero reads differently ("today"), then `key_one` / `key_other`.
const tryKey = (key, vars) => {
  try {
    return ft(key, vars);
  } catch (e) {
    if (!/^Missing copy key/.test(e.message)) throw e;
    return null;
  }
};
export const fc = (key, count, vars) =>
  (count === 0 && tryKey(`${key}_zero`, vars)) ||
  tryKey(`${key}_${count === 1 ? "one" : "other"}`, vars) ||
  ft(key, vars);

const ICONS = {
  err: ExclamationCircleIcon,
  info: InformationCircleIcon,
  ok: CheckCircleIcon,
  warn: ClockIcon,
};

export const Spinner = () => <span className="spin" aria-hidden="true" />;

// A button that keeps its place while it works: a spinner and the busy words,
// disabled so a second click can't fire a second request.
export const Btn = ({ busy, busyLabel, children, className = "", type = "button", disabled, ...rest }) => (
  <button
    type={type}
    className={`btn ${className}${busy ? " busy" : ""}`}
    disabled={disabled || busy}
    aria-busy={busy || undefined}
    {...rest}
  >
    {busy && <Spinner />}
    {busy && busyLabel ? busyLabel : children}
  </button>
);

// A message under one field.
export const Msg = ({ kind = "err", id, className = "", children }) => {
  const Icon = ICONS[kind];
  return (
    <p className={`msg ${kind} ${className}`.trim()} id={id}>
      <Icon className="ic" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
};

// A page-level problem or wait, above the form: one calm box.
export const Alert = ({ kind = "err", title, icon: IconOverride, children }) => {
  const Icon = IconOverride || ICONS[kind];
  return (
    <div className={`alert ${kind}`} role={kind === "err" ? "alert" : "status"}>
      <Icon className="ic" aria-hidden="true" />
      <div>
        {title && <b>{title}</b>}
        {children}
      </div>
    </div>
  );
};

// One photo slot (7A: fal.ai-generated guest photos, delivered by Morty). Until
// a slot's file lands, it draws the sheet's neutral frame instead.
export const Photo = ({ slot, className = "" }) => {
  const src = photoSrc(slot);
  return src ? (
    <img
      className={className}
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      style={PHOTO_POSITION[slot] ? { objectPosition: PHOTO_POSITION[slot] } : undefined}
    />
  ) : (
    <span className={`ph-slot ${className}`} aria-hidden="true" />
  );
};

const TICKET_ITEMS = ["t.li1", "t.li2", "t.li3", "t.li4"];
const STAMP_KEYS = ["t.s1", "t.s2", "t.s3"];

// The free event as a ticket: what you get, and a stamp per finished step.
export const Ticket = ({ stamps, title, host }) => (
  <>
    <div className="ticket-sh" />
    <div className="ticket">
      <div className="top">
        <div className="kick">
          <TicketIcon className="ic" aria-hidden="true" />
          {ft("t.kick")}
        </div>
        <h2>{title}</h2>
        {host !== undefined && <div className="host">{host}</div>}
        <ul>
          {TICKET_ITEMS.map((k) => (
            <li key={k}>
              <CheckIcon className="ic" aria-hidden="true" />
              {ft(k)}
            </li>
          ))}
        </ul>
      </div>
      <div className="perf" />
      <div className="stub">
        {STAMP_KEYS.map((k, i) => (
          <span key={k} className={`stamp ${stamps[i] || ""}`}>
            <i>{stamps[i] === "done" && <CheckIcon className="ic" aria-hidden="true" />}</i>
            {ft(k)}
          </span>
        ))}
      </div>
    </div>
  </>
);

// Below 1024 px the ticket folds into a strip; its three dots are the stamps.
export const TicketStrip = ({ done }) => (
  <div className="st-strip">
    <TicketIcon className="ic" aria-hidden="true" />
    <div className="sx">
      <div className="t">{ft("strip.t")}</div>
      <div className="row">
        <span className="s">{ft("strip.s")}</span>
        <span className="dots" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <i key={i} className={i < done ? "done" : ""}>
              {i < done && <CheckIcon className="ic" />}
            </i>
          ))}
        </span>
      </div>
    </div>
  </div>
);

// Stamp states for each step: Email, Phone, You.
export const STAMPS = {
  email: ["now", "", ""],
  phone: ["done", "now", ""],
  name: ["done", "done", "now"],
  done: ["done", "done", "done"],
};
